/**
 * Selection wiring on TanStack's `RowSelectionState`. Owns the
 * `RowSelectionState` ↔ `ILibraryListItem[]` translation so the consumer never
 * has to keep both shapes in sync.
 *
 * Observable contract preserved across the IMPL.B → cleanup arc:
 *   - `clearSelection` empties everything AND fires `onItemSelectionChange([])`
 *   - `markFolderNavigating()` swallows the next change emission
 *   - `markHeaderCheckboxClicked()` forces a global clear on the next change
 *     (covers the multi-page deselect-all that MUI's
 *     `keepNonExistentRowsSelected` would otherwise leak)
 *   - The `prevVisibleSelected > 1` heuristic still clears the WHOLE map even
 *     when the gesture wasn't tagged via the header-checkbox flag
 *   - `selectedItems` carries the FULL `ILibraryListItem` ref so per-type
 *     dispatch in ActionsBar (Unit 5) doesn't need a re-fetch
 */
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { useCallback, useEffect, useRef, useState } from "react";

import { ILibraryListItem } from "@/api";

import { libraryItemRowId } from "../utils/libraryRowId";
import { isForeignSharedSubject } from "../utils/folderTreeUtils";
import type { ILibraryRow } from "../components/documentListColumns.types";

/** TanStack-style updater (matches `Updater<RowSelectionState>`). */
export type RowSelectionUpdater =
    | RowSelectionState
    | ((prev: RowSelectionState) => RowSelectionState);

interface UseLibrarySelectionOptions {
    rows: ILibraryRow[];
    onItemSelectionChange?: (items: ILibraryListItem[]) => void;
}

export interface UseLibrarySelectionReturn {
    rowSelection: RowSelectionState;
    onRowSelectionChange: OnChangeFn<RowSelectionState>;
    /**
     * CROSS-PR CONTRACT (Unit 5 ActionsBar depends on this):
     * `selectedItems` carries the FULL {@link ILibraryListItem} reference, NOT
     * just the id. ActionsBar reads `singleItem.type` to dispatch the right
     * per-type Rename dialog (document / scope / comparison / folder). Reducing
     * to ids would force ActionsBar to re-fetch per click, racing against
     * streaming pagination. Do NOT "tidy" this to ids in a future refactor.
     */
    selectedItems: ILibraryListItem[];
    selectedCount: number;
    clearSelection: () => void;
    markFolderNavigating: () => void;
    markHeaderCheckboxClicked: () => void;
}

const EMPTY_SELECTION: RowSelectionState = Object.freeze(
    {}
) as RowSelectionState;

function countTruthy(state: RowSelectionState, ids: string[]): number {
    let count = 0;
    for (const id of ids) {
        if (state[id]) count += 1;
    }
    return count;
}

export function useLibrarySelection({
    rows,
    onItemSelectionChange,
}: UseLibrarySelectionOptions): UseLibrarySelectionReturn {
    const [rowSelection, setRowSelection] =
        useState<RowSelectionState>(EMPTY_SELECTION);
    const [selectedItems, setSelectedItems] = useState<ILibraryListItem[]>([]);

    const rowSelectionRef = useRef<RowSelectionState>(rowSelection);
    rowSelectionRef.current = rowSelection;

    const selectedItemsMapRef = useRef<Map<string, ILibraryListItem>>(
        new Map()
    );
    const knownForeignIdsRef = useRef(new Set<string>());
    for (const row of rows) {
        const id = libraryItemRowId(row._raw);
        if (isForeignSharedSubject(row._raw)) {
            knownForeignIdsRef.current.add(id);
        } else {
            knownForeignIdsRef.current.delete(id);
        }
    }
    const folderNavigatingRef = useRef(false);
    const headerCheckboxClickedRef = useRef(false);

    // Stable ref so the public callbacks don't churn when callers pass
    // anonymous functions inline.
    const onItemSelectionChangeRef = useRef(onItemSelectionChange);
    onItemSelectionChangeRef.current = onItemSelectionChange;

    useEffect(() => {
        const map = selectedItemsMapRef.current;
        const foreignIds = new Set(knownForeignIdsRef.current);
        for (const [id, item] of map) {
            if (isForeignSharedSubject(item)) foreignIds.add(id);
        }
        const staleIds = Object.keys(rowSelectionRef.current).filter((id) =>
            foreignIds.has(id)
        );
        if (
            staleIds.length === 0 &&
            ![...map.keys()].some((id) => foreignIds.has(id))
        ) {
            return;
        }
        for (const id of foreignIds) map.delete(id);
        const next = Object.fromEntries(
            Object.entries(rowSelectionRef.current).filter(
                ([id]) => !foreignIds.has(id)
            )
        );
        rowSelectionRef.current = next;
        setRowSelection(next);
        const items = Array.from(map.values());
        setSelectedItems(items);
        onItemSelectionChangeRef.current?.(items);
    }, [rows]);

    const applyEmptyState = useCallback(() => {
        rowSelectionRef.current = EMPTY_SELECTION;
        setRowSelection(EMPTY_SELECTION);
        selectedItemsMapRef.current.clear();
        setSelectedItems([]);
        onItemSelectionChangeRef.current?.([]);
    }, []);

    const clearSelection = useCallback(() => {
        applyEmptyState();
    }, [applyEmptyState]);

    const markFolderNavigating = useCallback(() => {
        folderNavigatingRef.current = true;
    }, []);

    const markHeaderCheckboxClicked = useCallback(() => {
        headerCheckboxClickedRef.current = true;
    }, []);

    const onRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
        (updater) => {
            // Folder row was clicked for navigation — swallow the change.
            if (folderNavigatingRef.current) {
                folderNavigatingRef.current = false;
                return;
            }

            const prev = rowSelectionRef.current;
            const proposed =
                typeof updater === "function" ? updater(prev) : updater;
            const map = selectedItemsMapRef.current;
            const foreignIds = new Set(knownForeignIdsRef.current);
            for (const [id, item] of map) {
                if (isForeignSharedSubject(item)) foreignIds.add(id);
            }
            const next = Object.fromEntries(
                Object.entries(proposed).filter(([id]) => !foreignIds.has(id))
            );

            const visibleIds = rows
                .filter((row) => !isForeignSharedSubject(row._raw))
                .map((row) => libraryItemRowId(row._raw));
            const newVisibleSelected = countTruthy(next, visibleIds);

            // Header-checkbox "deselect all" detection. Two paths, both must
            // clear the WHOLE selection map even if off-page entries remain:
            //   1. explicit flag set via `markHeaderCheckboxClicked()` —
            //      definitive, handles single-row pages
            //   2. `prevVisibleSelected > 1` heuristic — fallback for
            //      sweep-style gestures that miss the flag
            const wasHeaderCheckbox = headerCheckboxClickedRef.current;
            headerCheckboxClickedRef.current = false;

            if (newVisibleSelected === 0 && visibleIds.length > 0) {
                if (wasHeaderCheckbox) {
                    applyEmptyState();
                    return;
                }
                const prevVisibleSelected = countTruthy(prev, visibleIds);
                if (prevVisibleSelected > 1) {
                    applyEmptyState();
                    return;
                }
            }

            // Sync visible rows: add selected, remove deselected.
            // Using `libraryItemRowId(row._raw)` here (instead of the
            // already-built `row.id`) keeps the map keyed by the canonical
            // string built from the SAME helper the table's `getRowId`
            // resolves to in `useDocumentListTable`. If they ever drift,
            // selection persistence across pagination silently regresses
            // (Unit 2.RED #14 covers this).
            for (const row of rows) {
                const id = libraryItemRowId(row._raw);
                if (next[id] && !isForeignSharedSubject(row._raw)) {
                    map.set(id, row._raw);
                } else {
                    map.delete(id);
                }
            }

            // Drop any off-page items that are no longer selected — covers
            // programmatic full-clear via `onRowSelectionChange({})` and the
            // sweep-gesture fallback above.
            for (const [key, item] of map) {
                if (!next[key] || isForeignSharedSubject(item)) {
                    map.delete(key);
                }
            }

            const items = Array.from(map.values());
            setSelectedItems(items);
            rowSelectionRef.current = next;
            setRowSelection(next);
            onItemSelectionChangeRef.current?.(items);
        },
        [rows, applyEmptyState]
    );

    return {
        rowSelection,
        onRowSelectionChange,
        selectedItems,
        selectedCount: selectedItems.length,
        clearSelection,
        markFolderNavigating,
        markHeaderCheckboxClicked,
    };
}
